/**
 * Workflow Event Listener Service - Phase 5 Week 9-10
 * 
 * Listens for events (patient admission, device insertion, outbreak detection, scheduled time)
 * and automatically triggers workflows based on event-to-workflow mappings.
 */

import { db } from '../firebase';
import { 
  collection, 
  query, 
  where, 
  onSnapshot, 
  getDocs,
  Timestamp
} from 'firebase/firestore';
import { createWorkflowExecution, startWorkflowExecution } from './workflowExecutionService';
import workflowService from './workflowService';

/**
 * Event types that can trigger workflows
 */
export const EVENT_TYPES = {
  PATIENT_ADMISSION: 'patient_admission',
  PATIENT_TRANSFER: 'patient_transfer',
  PATIENT_DISCHARGE: 'patient_discharge',
  DEVICE_INSERTION: 'device_insertion',
  DEVICE_REMOVAL: 'device_removal',
  OUTBREAK_DETECTION: 'outbreak_detection',
  SCHEDULED_TIME: 'scheduled_time',
  RISK_ASSESSMENT: 'risk_assessment',
  COMPLIANCE_DEVIATION: 'compliance_deviation',
  PROTOCOL_VIOLATION: 'protocol_violation',
  ATP_POSITIVE: 'ATP_POSITIVE',
  LAB_RESULT_POSITIVE: 'LAB_RESULT_POSITIVE',
  COMPLIANCE_FAILURE: 'COMPLIANCE_FAILURE'
};

/**
 * Event listener class
 */
export class WorkflowEventListener {
  constructor() {
    this.listeners = new Map(); // Map of event type -> unsubscribe function
    this.workflowMappings = new Map(); // Map of event type -> workflow definitions
    this.isInitialized = false;
    // Deduplication: tracks cycle keys for already-fired scheduled workflows
    // Key: `${workflowId}::${cycleKey}`, prevents double-firing in same cycle
    this.scheduledFiredCycles = new Map();
  }

  /**
   * Uniform error handler for onSnapshot listeners.
   * - Index not ready / missing → single clear warning, no noise
   * - Collection not found → silenced (collection may not exist yet)
   * - Other → console.error
   */
  _handleListenerError(listenerName, error) {
    const msg = error?.message || String(error);
    if (msg.includes('requires an index') || msg.includes('The query requires an index')) {
      console.warn(
        `⏳ [${listenerName}] Firestore index not ready yet — listener paused until index builds. ` +
        `This resolves automatically. Check Firebase Console → Firestore → Indexes.`
      );
    } else if (
      msg.includes('NOT_FOUND') ||
      msg.includes('does not exist') ||
      msg.includes('Missing or insufficient permissions')
    ) {
      console.debug(`ℹ️ [${listenerName}] Collection unavailable or no permissions — skipping.`);
    } else {
      console.error(`❌ [${listenerName}] Listener error:`, msg);
    }
  }

  /**
   * Initialize event listeners for all event types
   */
  async initialize(userId = 'system') {
    if (this.isInitialized) {
      console.warn('⚠️ WorkflowEventListener already initialized');
      return;
    }

    console.log('🔄 Initializing Workflow Event Listeners...');
    
    try {
      // Load workflow-to-event mappings
      await this.loadWorkflowMappings();
      
      // Set up listeners for each event type
      await this.setupPatientAdmissionListener(userId);
      await this.setupDeviceInsertionListener(userId);
      await this.setupOutbreakDetectionListener(userId);
      await this.setupComplianceDeviationListener(userId);
      await this.setupRiskAssessmentListener(userId);
      await this.setupWorkflowEventsListener(userId);
      
      // Set up scheduled time triggers (cron-like)
      this.setupScheduledTimeTriggers(userId);
      
      this.isInitialized = true;
      console.log('✅ Workflow Event Listeners initialized');
    } catch (error) {
      console.error('❌ Error initializing event listeners:', error);
      throw error;
    }
  }

  /**
   * Load workflow-to-event mappings from Firestore
   */
  async loadWorkflowMappings() {
    try {
      const workflows = await workflowService.getAllWorkflowDefinitions();
      
      // Build mappings: event type -> workflows that listen to it
      workflows.forEach(workflow => {
        if (workflow.triggers && Array.isArray(workflow.triggers)) {
          workflow.triggers.forEach(trigger => {
            if (trigger.trigger_type === 'event' && trigger.event_type) {
              const eventType = trigger.event_type;
              
              if (!this.workflowMappings.has(eventType)) {
                this.workflowMappings.set(eventType, []);
              }
              
              this.workflowMappings.get(eventType).push({
                workflow_id: workflow.id,
                workflow_code: workflow.workflow_code,
                workflow_name: workflow.workflow_name,
                trigger_config: trigger
              });
            }
          });
        }
      });
      
      console.log(`📋 Loaded ${this.workflowMappings.size} event-to-workflow mappings`);
    } catch (error) {
      console.error('Error loading workflow mappings:', error);
      throw error;
    }
  }

  /**
   * Setup listener for patient admission events
   */
  async setupPatientAdmissionListener(userId) {
    const patientsRef = collection(db, 'patients');
    
    // Listen for new patient admissions
    const q = query(
      patientsRef,
      where('status', '==', 'admitted')
    );
    
    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        snapshot.docChanges().forEach(async (change) => {
          if (change.type === 'added' || change.type === 'modified') {
            const patient = { id: change.doc.id, ...change.doc.data() };
            
            // Check if patient was just admitted (new document or status change)
            const admissionDate = patient.admissionDate?.toDate ? patient.admissionDate.toDate() : new Date(patient.admissionDate);
            const now = new Date();
            const timeSinceAdmission = now - admissionDate;
            
            // Only trigger if admission happened in last 5 minutes (avoid duplicate triggers)
            if (timeSinceAdmission < 5 * 60 * 1000) {
              await this.handleEvent(EVENT_TYPES.PATIENT_ADMISSION, {
                patient_id: patient.id,
                patient_name: patient.name || `${patient.first_name} ${patient.last_name}`,
                department_id: patient.departmentId,
                room_id: patient.roomId,
                bed_id: patient.bedId,
                admission_date: admissionDate,
                patient_data: patient
              }, userId);
            }
          }
        });
      },
      (error) => this._handleListenerError('PatientAdmission', error)
    );
    this.listeners.set(EVENT_TYPES.PATIENT_ADMISSION, unsubscribe);
    console.log('👂 Listening for patient admission events');
  }

  /**
   * Setup listener for device insertion events
   */
  async setupDeviceInsertionListener(userId) {
    // Note: Device insertion events would come from a device_tracking collection
    // For now, we'll listen to protocol compliance events related to device insertion
    const complianceEventsRef = collection(db, 'protocol_compliance_events');
    
    const q = query(
      complianceEventsRef,
      where('eventType', '==', 'device_insertion'),
      where('metadata.isActive', '==', true)
    );
    
    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        snapshot.docChanges().forEach(async (change) => {
          if (change.type === 'added') {
            const event = { id: change.doc.id, ...change.doc.data() };
            
            await this.handleEvent(EVENT_TYPES.DEVICE_INSERTION, {
              event_id: event.id,
              device_type: event.eventDetails?.equipmentUsed?.[0] || 'unknown',
              patient_id: event.patient?.patientId || null,
              location_id: event.location?.locationId || null,
              department_id: event.location?.departmentId || null,
              insertion_date: event.eventDetails?.timestamp?.toDate ? event.eventDetails.timestamp.toDate() : new Date(),
              event_data: event
            }, userId);
          }
        });
      },
      (error) => this._handleListenerError('DeviceInsertion', error)
    );
    this.listeners.set(EVENT_TYPES.DEVICE_INSERTION, unsubscribe);
    console.log('👂 Listening for device insertion events');
  }

  /**
   * Setup listener for outbreak detection events
   */
  async setupOutbreakDetectionListener(userId) {
    // Listen for outbreak detection events (could be from biological_entities or risk_alerts)
    const riskAlertsRef = collection(db, 'risk_alerts');
    
    const q = query(
      riskAlertsRef,
      where('severity', '==', 'critical'),
      where('status', '==', 'active')
    );
    
    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        snapshot.docChanges().forEach(async (change) => {
          if (change.type === 'added') {
            const alert = { id: change.doc.id, ...change.doc.data() };
            
            // Check if it's an outbreak-related alert
            if (alert.alert_type === 'outbreak' || alert.title?.toLowerCase().includes('outbreak')) {
              await this.handleEvent(EVENT_TYPES.OUTBREAK_DETECTION, {
                alert_id: alert.id,
                outbreak_type: alert.alert_type || 'general',
                location_id: alert.location?.locationId || null,
                department_id: alert.location?.departmentId || null,
                severity: alert.severity,
                detected_at: alert.createdAt?.toDate ? alert.createdAt.toDate() : new Date(),
                alert_data: alert
              }, userId);
            }
          }
        });
      },
      (error) => this._handleListenerError('OutbreakDetection', error)
    );
    this.listeners.set(EVENT_TYPES.OUTBREAK_DETECTION, unsubscribe);
    console.log('👂 Listening for outbreak detection events');
  }

  /**
   * Setup listener for compliance deviation events
   */
  async setupComplianceDeviationListener(userId) {
    const complianceEventsRef = collection(db, 'protocol_compliance_events');
    
    const q = query(
      complianceEventsRef,
      where('complianceMetrics.qualityScore', '<', 0.7), // Less than 70% compliance
      where('metadata.isActive', '==', true)
    );
    
    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        snapshot.docChanges().forEach(async (change) => {
          if (change.type === 'added') {
            const event = { id: change.doc.id, ...change.doc.data() };
            
            await this.handleEvent(EVENT_TYPES.COMPLIANCE_DEVIATION, {
              event_id: event.id,
              protocol_id: event.protocol?.protocolId || null,
              compliance_score: event.complianceMetrics?.qualityScore || 0,
              location_id: event.location?.locationId || null,
              department_id: event.location?.departmentId || null,
              deviation_details: event.complianceMetrics?.deviations || [],
              detected_at: event.eventDetails?.timestamp?.toDate ? event.eventDetails.timestamp.toDate() : new Date(),
              event_data: event
            }, userId);
          }
        });
      },
      (error) => this._handleListenerError('ComplianceDeviation', error)
    );
    this.listeners.set(EVENT_TYPES.COMPLIANCE_DEVIATION, unsubscribe);
    console.log('👂 Listening for compliance deviation events');
  }

  /**
   * Setup listener for risk assessment events
   */
  async setupRiskAssessmentListener(userId) {
    const riskAssessmentsRef = collection(db, 'risk_assessments');
    
    const q = query(
      riskAssessmentsRef,
      where('riskLevel', 'in', ['High', 'Critical', 'Extreme'])
    );
    
    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        snapshot.docChanges().forEach(async (change) => {
          if (change.type === 'added') {
            const assessment = { id: change.doc.id, ...change.doc.data() };
            
            await this.handleEvent(EVENT_TYPES.RISK_ASSESSMENT, {
              assessment_id: assessment.id,
              risk_level: assessment.riskLevel,
              risk_score: assessment.finalRiskScore || 0,
              patient_id: assessment.patientId || null,
              location_id: assessment.locationId || null,
              department_id: assessment.departmentId || null,
              assessed_at: assessment.assessedAt?.toDate ? assessment.assessedAt.toDate() : new Date(),
              assessment_data: assessment
            }, userId);
          }
        });
      },
      (error) => this._handleListenerError('RiskAssessment', error)
    );
    this.listeners.set(EVENT_TYPES.RISK_ASSESSMENT, unsubscribe);
    console.log('👂 Listening for risk assessment events');
  }

  /**
   * Setup listener for manually dispatched workflow events (simulator, integrations)
   * Documents written to workflow_events collection are picked up here and routed
   * through handleEvent using event_code as the event type key.
   */
  async setupWorkflowEventsListener(userId) {
    const workflowEventsRef = collection(db, 'workflow_events');
    // Only process events dispatched after this listener started — prevents
    // re-triggering historical events on every login.
    const listenFrom = Timestamp.now();

    const unsubscribe = onSnapshot(
      query(workflowEventsRef, where('triggered_at', '>', listenFrom)),
      async (snapshot) => {
        snapshot.docChanges().forEach(async (change) => {
          if (change.type !== 'added') return;
          const event = { id: change.doc.id, ...change.doc.data() };
          if (!event.event_code) return;

          await this.handleEvent(event.event_code, {
            event_id: event.id,
            entity_type: event.entity_type || null,
            entity_id: event.entity_id || null,
            source: event.source || 'unknown',
            notes: event.notes || null,
            triggered_at: event.triggered_at?.toDate ? event.triggered_at.toDate() : new Date(),
            raw_event: event
          }, event.triggered_by || userId);
        });
      },
      (error) => this._handleListenerError('WorkflowEvents', error)
    );

    this.listeners.set('workflow_events', unsubscribe);
    console.log('👂 Listening for workflow_events (simulator + integrations)');
  }

  /**
   * Setup scheduled time triggers (cron-like, checks every minute)
   */
  setupScheduledTimeTriggers(userId) {
    setInterval(async () => {
      try {
        const workflows = await workflowService.getAllWorkflowDefinitions();

        for (const workflow of workflows) {
          if (!workflow.status || workflow.status === 'inactive') continue;
          if (!Array.isArray(workflow.triggers)) continue;

          for (const trigger of workflow.triggers) {
            if (trigger.trigger_type !== 'scheduled' || !trigger.schedule) continue;

            const { matches, cycleKey } = this.evaluateSchedule(trigger.schedule);
            if (!matches) continue;

            const dedupeKey = `${workflow.id}::${cycleKey}`;
            if (this.scheduledFiredCycles.has(dedupeKey)) continue; // already fired this cycle

            this.scheduledFiredCycles.set(dedupeKey, Date.now());
            // Prune old entries to avoid memory leak (keep only last 200)
            if (this.scheduledFiredCycles.size > 200) {
              const oldest = [...this.scheduledFiredCycles.entries()]
                .sort((a, b) => a[1] - b[1])
                .slice(0, 50);
              oldest.forEach(([k]) => this.scheduledFiredCycles.delete(k));
            }

            console.log(`⏰ Scheduled trigger fired for workflow "${workflow.workflow_name || workflow.id}" (cycle: ${cycleKey})`);

            try {
              const context = {
                triggered_by: 'scheduled',
                event_type: EVENT_TYPES.SCHEDULED_TIME,
                schedule: trigger.schedule,
                cycle_key: cycleKey,
              };
              const execution = await createWorkflowExecution(workflow.id, context, userId);
              await startWorkflowExecution(execution.id, userId);
              console.log(`▶️ Scheduled execution started: ${execution.execution_code}`);
            } catch (execErr) {
              console.error(`❌ Failed to start scheduled workflow "${workflow.id}":`, execErr.message);
            }
          }
        }
      } catch (error) {
        console.error('Error checking scheduled triggers:', error);
      }
    }, 60 * 1000);

    console.log('⏰ Scheduled time triggers active (checking every minute)');
  }

  /**
   * Evaluate if a schedule matches right now.
   * Returns { matches: boolean, cycleKey: string }
   * cycleKey uniquely identifies the current cycle for deduplication.
   */
  evaluateSchedule(schedule) {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hour = now.getHours();
    const minute = now.getMinutes();
    const dayOfWeek = now.getDay(); // 0=Sun, 1=Mon … 6=Sat

    const timeMatch = (timeStr) => {
      if (!timeStr) return false;
      const [h, m] = timeStr.split(':').map(Number);
      return hour === h && minute === m;
    };

    switch (schedule.frequency) {
      case 'daily': {
        const matches = timeMatch(schedule.time);
        return { matches, cycleKey: `daily-${year}-${month}-${day}` };
      }
      case 'weekly': {
        // days_of_week: array of day numbers [0-6], or legacy single dayOfWeek number
        const targetDays = Array.isArray(schedule.days_of_week)
          ? schedule.days_of_week.map(Number)
          : schedule.dayOfWeek != null ? [Number(schedule.dayOfWeek)] : [1];
        const matches = targetDays.includes(dayOfWeek) && timeMatch(schedule.time);
        // ISO week number for cycle key
        const weekNum = Math.ceil(((now - new Date(year, 0, 1)) / 86400000 + 1) / 7);
        return { matches, cycleKey: `weekly-${year}-W${weekNum}-${dayOfWeek}` };
      }
      case 'monthly': {
        const targetDay = Number(schedule.day_of_month || schedule.dayOfMonth || 1);
        const matches = now.getDate() === targetDay && timeMatch(schedule.time);
        return { matches, cycleKey: `monthly-${year}-${month}-${targetDay}` };
      }
      case 'hourly': {
        const intervalHours = Number(schedule.interval_hours || 1);
        const matches = hour % intervalHours === 0 && minute === 0;
        const slotHour = Math.floor(hour / intervalHours) * intervalHours;
        return { matches, cycleKey: `hourly-${year}-${month}-${day}-${slotHour}h` };
      }
      default:
        return { matches: false, cycleKey: 'unknown' };
    }
  }

  /**
   * Handle an event and trigger matching workflows
   */
  async handleEvent(eventType, eventData, userId) {
    try {
      console.log(`🔔 Event received: ${eventType}`, eventData);
      
      // Get workflows that listen to this event type
      const workflows = this.workflowMappings.get(eventType) || [];
      
      if (workflows.length === 0) {
        console.log(`ℹ️ No workflows configured for event type: ${eventType}`);
        return;
      }
      
      // Trigger each matching workflow
      for (const workflowMapping of workflows) {
        try {
          // Check trigger conditions if specified
          const shouldTrigger = this.evaluateTriggerConditions(
            workflowMapping.trigger_config,
            eventData
          );
          
          if (!shouldTrigger) {
            console.log(`⏭️ Workflow ${workflowMapping.workflow_code} skipped (conditions not met)`);
            continue;
          }
          
          // Create workflow execution context
          const context = {
            triggered_by: 'event',
            event_type: eventType,
            event_data: eventData,
            scheduled_time: null
          };
          
          // Create and start workflow execution
          const execution = await createWorkflowExecution(
            workflowMapping.workflow_id,
            context,
            userId
          );
          
          await startWorkflowExecution(execution.id, userId);
          
          console.log(`✅ Triggered workflow: ${workflowMapping.workflow_code} (${execution.id})`);
        } catch (error) {
          console.error(`❌ Error triggering workflow ${workflowMapping.workflow_code}:`, error);
        }
      }
    } catch (error) {
      console.error(`❌ Error handling event ${eventType}:`, error);
    }
  }

  /**
   * Evaluate trigger conditions
   */
  evaluateTriggerConditions(triggerConfig, eventData) {
    if (!triggerConfig.conditions || triggerConfig.conditions.length === 0) {
      return true; // No conditions = always trigger
    }
    
    // Evaluate each condition
    for (const condition of triggerConfig.conditions) {
      const fieldValue = this.getNestedValue(eventData, condition.field);
      
      let result = false;
      switch (condition.operator) {
        case 'equals':
          result = fieldValue === condition.value;
          break;
        case 'not_equals':
          result = fieldValue !== condition.value;
          break;
        case 'greater_than':
          result = Number(fieldValue) > Number(condition.value);
          break;
        case 'less_than':
          result = Number(fieldValue) < Number(condition.value);
          break;
        case 'contains':
          result = String(fieldValue).includes(condition.value);
          break;
        case 'in':
          result = Array.isArray(condition.value) && condition.value.includes(fieldValue);
          break;
        default:
          result = true;
      }
      
      // AND logic: all conditions must be true
      if (triggerConfig.logic === 'AND' && !result) {
        return false;
      }
      
      // OR logic: at least one condition must be true
      if (triggerConfig.logic === 'OR' && result) {
        return true;
      }
    }
    
    return triggerConfig.logic === 'AND'; // All conditions passed for AND
  }

  /**
   * Get nested value from object (e.g., "location.department_id")
   */
  getNestedValue(obj, path) {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }

  /**
   * Manually trigger an event (for testing or manual triggers)
   */
  async triggerEvent(eventType, eventData, userId = 'system') {
    await this.handleEvent(eventType, eventData, userId);
  }

  /**
   * Stop all event listeners
   */
  stop() {
    this.listeners.forEach((unsubscribe, eventType) => {
      unsubscribe();
      console.log(`🛑 Stopped listener for: ${eventType}`);
    });
    
    this.listeners.clear();
    this.isInitialized = false;
    console.log('🛑 All event listeners stopped');
  }

  /**
   * Reload workflow mappings (call when workflows are added/updated)
   */
  async reloadMappings() {
    this.workflowMappings.clear();
    await this.loadWorkflowMappings();
    console.log('🔄 Workflow mappings reloaded');
  }
}

// Export singleton instance
export const workflowEventListener = new WorkflowEventListener();

// Auto-initialize on import (optional - can be called manually)
// workflowEventListener.initialize().catch(console.error);


